import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Clock, FileText, DollarSign, Calendar, Award } from "lucide-react";
import { EmployeeOverview } from "./EmployeeOverview";
import { EmployeeTimeTracking } from "./EmployeeTimeTracking";
import { EmployeeDocumentManagement } from "./EmployeeDocumentManagement";
import { EmployeePayrollView } from "./EmployeePayrollView";
import { EmployeeTimeline } from "./EmployeeTimeline";
import { EmployeeCertificationView } from "./EmployeeCertificationView";

interface Employee {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  hire_date: string;
  is_active: boolean;
  role: 'staff' | 'admin';
  created_at: string;
}

interface EmployeeProfile {
  hourly_rate: number | null;
  salary_type: string | null;
  overtime_rate: number | null;
  payment_method: string | null;
  avatar_url: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relation: string | null;
  medical_notes: string | null;
}

interface TimeEntry {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: number | null;
  notes: string | null;
}

interface PayrollCalculation {
  id: string;
  period_start: string;
  period_end: string;
  total_hours: number;
  regular_hours: number;
  overtime_hours: number;
  gross_pay: number;
  net_pay: number;
  status: string;
  created_at: string;
}

interface Document {
  id: string;
  name: string;
  file_url: string;
  category: string;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
  expires_at: string | null;
  is_required: boolean | null;
  uploaded_by: string | null;
}

interface EmployeeProfileTabsProps {
  employee: Employee;
  profile: EmployeeProfile | null;
  timeEntries: TimeEntry[];
  payrollHistory: PayrollCalculation[];
  documents: Document[];
  onDataUpdate: () => void;
}

export const EmployeeProfileTabs: React.FC<EmployeeProfileTabsProps> = ({
  employee,
  profile,
  timeEntries,
  payrollHistory,
  documents,
  onDataUpdate
}) => {
  return (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList className="grid w-full grid-cols-6">
        <TabsTrigger value="overview" className="flex items-center gap-2">
          <User className="h-4 w-4" />
          <span className="hidden sm:inline">Overview</span>
        </TabsTrigger>
        <TabsTrigger value="time" className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span className="hidden sm:inline">Time</span>
        </TabsTrigger>
        <TabsTrigger value="payroll" className="flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          <span className="hidden sm:inline">Payroll</span>
        </TabsTrigger>
        <TabsTrigger value="documents" className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          <span className="hidden sm:inline">Documents</span>
        </TabsTrigger>
        <TabsTrigger value="timeline" className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span className="hidden sm:inline">Timeline</span>
        </TabsTrigger>
        <TabsTrigger value="certifications" className="flex items-center gap-2">
          <Award className="h-4 w-4" />
          <span className="hidden sm:inline">Certs</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="mt-6">
        <EmployeeOverview
          employee={employee}
          profile={profile}
          onDataUpdate={onDataUpdate}
        />
      </TabsContent>

      <TabsContent value="time" className="mt-6">
        <EmployeeTimeTracking
          employee={employee}
          timeEntries={timeEntries}
          onDataUpdate={onDataUpdate}
        />
      </TabsContent>

      <TabsContent value="payroll" className="mt-6">
        <EmployeePayrollView
          employee={employee}
          profile={profile}
          payrollHistory={payrollHistory}
          onDataUpdate={onDataUpdate}
        />
      </TabsContent>

      <TabsContent value="documents" className="mt-6">
        <EmployeeDocumentManagement
          employee={employee}
          documents={documents}
          onDataUpdate={onDataUpdate}
        />
      </TabsContent>

      <TabsContent value="timeline" className="mt-6">
        <EmployeeTimeline
          employee={employee}
          timeEntries={timeEntries}
          payrollHistory={payrollHistory}
        />
      </TabsContent>

      <TabsContent value="certifications" className="mt-6">
        <EmployeeCertificationView
          employee={employee}
          onDataUpdate={onDataUpdate}
        />
      </TabsContent>
    </Tabs>
  );
};